/* eslint-disable max-len */
import React, { useEffect, useState, Suspense } from 'react'
import { useInput, useBoolean } from 'react-hanger/array'
import { Flex, Box } from 'reflexbox'
import { Auth0Lock } from 'auth0-lock'

import { Link, useHistory, useLocation } from 'react-router-dom'
import axios from 'axios'
// Components
import { validateEmail } from '../../utilities/base'
import InputText from '../../components/form/InputText'
import Button from '../../components/form/Button'
import SignupUsers from './2023/Signup/signup_users.png'

// logos
import logoAmazon from './2023/Signup/client-logos/amazon-logo.png'
import logoNvidia from './2023/Signup/client-logos/nvidia-logo.png'
import logoPG from './2023/Signup/client-logos/procter-gamble-logo.png'
import logoIBM from './2023/Signup/client-logos/ibm-logo.png'
import logoDeloitte from './2023/Signup/client-logos/deloitte-logo.png'
import logoSalesforce from './2023/Signup/client-logos/salesforce-logo.png'
import logoRoche from './2023/Signup/client-logos/roche-logo.png'
import logoIntuit from './2023/Signup/client-logos/intuit-logo.png'
import logoCostco from './2023/Signup/client-logos/costco-logo.png'

import loop11Logo from '../../components/logo.svg'

import { useAlerts } from '../../components/alert_anon_manager'

const PasswordStrengthBar = React.lazy(() => import('react-password-strength-bar'))

const Signup2023 = () => {
  const urlParams = new URLSearchParams(window.location.search)

  const { showAlert } = useAlerts()
  const [loading, loadingActions] = useBoolean(false)

  const [[email], emailActions] = useInput(urlParams.get('email') || '')
  const [[password], passwordActions] = useInput('')
  const [[referralCode], referralCodeActions] = useInput('')
  const [haveReferralCode, haveReferralCodeActions] = useBoolean(false)

  const [errorObj, setErrorObj] = useState(null)
  const [errorMsg, setErrorMsg] = useState(null)

  const location = useLocation()
  const history = useHistory()

  const canEnableLoginBtn = () => {
    return !loading && email && validateEmail(email) && password
  }

  const submitForm = (e) => {
    e.preventDefault()
    loadingActions.setTrue()
    setErrorObj(null)
    setErrorMsg(null)
    axios
      .post(`/api/account/auth/signup/${window.location.search}`, {
        email,
        password,
        referral_code: referralCode,
      })
      .then((resp) => {
        window.location.href = resp.data.redirect_url || '/dashboard/'
      })
      .catch((error) => {
        if (error.response && error.response.data) {
          const resp = error.response.data
          if (resp.detail) {
            setErrorMsg(resp.detail)
          } else {
            setErrorObj(resp)
          }
        }
      })
      .finally(() => {
        loadingActions.setFalse()
      })
  }

  useEffect(() => {
    const queryParamError = urlParams.get('error')
    // eslint-disable-next-line max-len
    const queryParamErrorDesc =
      urlParams.get('error_description') || 'There was an authentication error. Please try again.'

    if (queryParamError) {
      showAlert('ERROR', queryParamErrorDesc, true)
    }

    const nextParam = urlParams.get('next')
    let redirectUrl = window.AUTH0_CALLBACK_URL
    if (nextParam) {
      // Encodes characters such as ?,=,/,&,:
      redirectUrl += `?next=${encodeURIComponent(nextParam)}`
    }

    // Initializing our Auth0Lock
    const lock = new Auth0Lock(window.AUTH0_CLIENT_ID, window.AUTH0_DOMAIN, {
      rememberLastLogin: true,
      allowSignUp: true,
      signUp: true,
      loginAfterSignUp: true,

      auth: {
        redirectUrl,
        responseType: 'code',
        params: {
          // scope: 'openid profile'
        },
      },
      allowedConnections: ['google-oauth2', 'windowslive', 'linkedin'],
      container: 'auth0-login-container',

      // See https://github.com/auth0/lock/blob/master/src/i18n/en.js
      languageDictionary: {
        title: '',
        loginWithLabel: '',
        loginSubmitLabel: 'Sign up with Single Sign-On (SSO)',
        error: {
          login: {
            'hrd.not_matching_email':
              // eslint-disable-next-line max-len
              'Single Sign-On has not been configured for your email address or domain yet. Try a different login method or contact your administrator.',
          },
        },
      },
      theme: {
        primaryColor: '#00a9a7',
      },
      prefill: {
        email,
      },
    })

    lock.show()

    // returned function will be called on component unmount
    return () => {
      lock.hide()
    }
  }, [])

  return (
    <form onSubmit={submitForm}>
      <Flex flexWrap="wrap" justifyContent="center">
        <Box width={[1, 2 / 5]} className="signup-container-wrapper">
          <div className="signup-container-2023">
            <div className="intro">
              <img src={loop11Logo} alt="Loop11" className="loop11-logo" />

              <h3 className="title">Create your free account</h3>
              <ul className="why-signup">
                <li>
                  <i className="far fa-check-circle" /> 14-day trial with access to Enterprise plan
                  features
                </li>
                <li>
                  <i className="far fa-check-circle" /> No credit card or contracts required
                </li>
                <li>
                  <i className="far fa-usd-circle" /> Like to get paid for testing websites?{' '}
                  <a href="/dashboard/cint/apply/" className="link">
                    Apply here
                  </a>
                </li>
              </ul>
            </div>

            <h5 className="social-title">Sign up with a business account</h5>
            <div id="auth0-login-container" />

            <div className="separator">
              <hr className="separator-line" />
              <span className="separator-text">Or create an account with email</span>
            </div>

            <div className="signup-email-wrapper">
              <div>
                <InputText
                  type="text"
                  helpText="Use a business email address. Personal or disposable ones cannot be used."
                  label=""
                  name="email"
                  value={email}
                  placeholder="username@your-business.com"
                  handleChange={emailActions.onChange}
                  blurOnly
                  className="inpt-signup"
                  error={(email && !validateEmail(email)) || (errorObj && errorObj.email)}
                  errorMsg={(errorObj && errorObj.email && errorObj.email[0]) || 'Invalid email'}
                  focus={(errorObj && errorObj.email) || (!email && !urlParams.get('email'))}
                  disabled={loading}
                />
                <InputText
                  type="password"
                  helpText="Use 12 or more characters with a mix of upper & lowercase letters, numbers, and special characters."
                  label=""
                  name="password"
                  value={password}
                  placeholder="Password"
                  handleChange={passwordActions.onChange}
                  className="inpt-signup sentry-mask"
                  error={errorObj && errorObj.password}
                  errorMsg={errorObj && errorObj.password && errorObj.password[0]}
                  focus={(errorObj && errorObj.password) || urlParams.get('email')}
                  disabled={loading}
                />
                <Suspense fallback={<span className="ellipsis-animation" />}>
                  <PasswordStrengthBar
                    password={password}
                    scoreWordClassName="password-strength-scoreword"
                    shortScoreWord="Strength: -"
                    scoreWords={[
                      'Strength: very weak',
                      'Strength: weak',
                      'Strength: okay',
                      'Strength: good',
                      'Strength: strong',
                    ]}
                  />
                </Suspense>

                {haveReferralCode && (
                  <InputText
                    type="text"
                    label=""
                    name="referral_code"
                    value={referralCode}
                    placeholder="Referral Code"
                    handleChange={referralCodeActions.onChange}
                    className="inpt-signup"
                    error={null}
                    errorMsg=""
                    disabled={loading}
                  />
                )}
                {!haveReferralCode && (
                  <p>
                    {/* eslint-disable-next-line max-len */}
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid,jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
                    <a
                      href="#"
                      onClick={() => {
                        haveReferralCodeActions.toggle()
                      }}
                    >
                      Have a Referral Code?
                    </a>
                  </p>
                )}

                <p className="error">{errorMsg}</p>
                <Button
                  type="submit"
                  className="signup"
                  label="Sign up"
                  clickHandler={submitForm}
                  disabled={!canEnableLoginBtn()}
                />
                <p>
                  By signing up you agree to our{' '}
                  <a href="https://www.loop11.com/terms/">Terms of Use</a> and{' '}
                  <a href="https://www.loop11.com/privacy/">Privacy Policy</a>.
                </p>
                <p>
                  Already have an account?{' '}
                  <Link to={{ pathname: '/login/', search: location.search }}>Log in instead</Link>.
                </p>
              </div>
            </div>

            <div className="separator">
              <hr className="separator-line" />
            </div>

            <div>
              <Button
                className="outline slim"
                label="Single Sign-On (SSO)"
                clickHandler={() => {
                  history.push(`/login/sso/`)
                }}
              />
            </div>
          </div>
        </Box>

        <Box width={[1, 3 / 5]} className="marketing-container">
          <div className="half-circle">
            <div className="marketing-content">
              <img className="signup-users" src={SignupUsers} alt="Loop11 users" />
              <h3 className="title">
                {/* eslint-disable-next-line react/no-unescaped-entities */}
                The online usability testing platform of choice for some of the world's most
                successful brands
              </h3>
              <div className="customers-brands">
                <Flex flexWrap="wrap" justifyContent="center">
                  <Box width={[1 / 2, 1 / 3]} className="brand-container">
                    <img className="brand-logo" src={logoAmazon} alt="Amazon" />
                  </Box>
                  <Box width={[1 / 2, 1 / 3]} className="brand-container">
                    <img className="brand-logo" src={logoNvidia} alt="Nvidia" />
                  </Box>
                  <Box width={[1 / 2, 1 / 3]} className="brand-container">
                    <img className="brand-logo" src={logoPG} alt="P&G" />
                  </Box>
                  <Box width={[1 / 2, 1 / 3]} className="brand-container">
                    <img className="brand-logo" src={logoIBM} alt="IBM" />
                  </Box>
                  <Box width={[1 / 2, 1 / 3]} className="brand-container">
                    <img className="brand-logo" src={logoDeloitte} alt="Deloitte" />
                  </Box>
                  <Box width={[1 / 2, 1 / 3]} className="brand-container">
                    <img className="brand-logo" src={logoSalesforce} alt="Salesforce" />
                  </Box>
                  <Box width={[1 / 2, 1 / 3]} className="brand-container">
                    <img className="brand-logo" src={logoRoche} alt="Roche" />
                  </Box>
                  <Box width={[1 / 2, 1 / 3]} className="brand-container">
                    <img className="brand-logo" src={logoIntuit} alt="Intuit" />
                  </Box>
                  <Box width={[1 / 2, 1 / 3]} className="brand-container">
                    <img className="brand-logo" src={logoCostco} alt="Costco" />
                  </Box>
                </Flex>
              </div>
            </div>
          </div>
        </Box>
      </Flex>
    </form>
  )
}

export default Signup2023
